<template>
  <div class="viewing-appointments-table">
    <h5 class="mb-3">
      {{ $t('message.marketingObjects.viewingAppointments.tableTitle') }}
    </h5>
    <div class="row mb-2 align-items-center">
      <div class="col-12 col-md-3 mb-2 mb-md-0">
        <coozzy-dropdown
          design="green"
          size="sm"
          class="w-100"
          :text="selectedEntriesText">
          <div class="row m-0">
            <coozzy-dropdown-item
              class="col pr-0 pl-0"
              :disabled="!canCancelAppointments"
              @click="onCancelAppointments">
              {{ $t('message.marketingObjects.viewingAppointments.actions.cancel') }}
            </coozzy-dropdown-item>
            <div
              v-if="showCancelScheduledOnlyTooltip"
              class="pr-3">
              <coozzy-info-circle-icon
                v-b-tooltip.hover.html="$t('message.marketingObjects.viewingAppointments.actions.cancelScheduledOnlyTooltip')"
                class="mt-2" />
            </div>
          </div>
          <coozzy-dropdown-item
            :disabled="!canWriteSerialEmail"
            @click="onSerialEmail">
            {{ $t('message.generic.serialEmail') }}
          </coozzy-dropdown-item>
        </coozzy-dropdown>
      </div>
      <div class="col-12 col-md-2 offset-md-7">
        <coozzy-form-select
          v-model="nbrPerPage"
          name="numberEntries"
          class="select-entries float-right">
          <option :value="5">
            5 {{ $t('message.generic.entries') }}
          </option>
          <option :value="10">
            10 {{ $t('message.generic.entries') }}
          </option>
        </coozzy-form-select>
      </div>
    </div>
    <b-table
      ref="appointmentsTable"
      bordered
      hover
      responsive="true"
      stacked="md"
      selectable
      select-mode="single"
      :busy="busy"
      :fields="fields"
      :items="pagedAppointments"
      :tbody-tr-class="rowClass"
      class="mb-0"
      @row-clicked="onRowClicked">
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <coozzy-spinner />
        </div>
      </template>
      <template #head(checkbox)>
        <coozzy-form-checkbox
          ref="header_checkbox"
          :initial="allPageSelected"
          @change="onSelectAll" />
      </template>
      <template #cell(checkbox)="data">
        <coozzy-form-checkbox
          :id="'viewing-appointment-check-' + data.item.id"
          :initial="selectedEntries.includes(data.item.id)"
          @change="(value) => selectAppointment(data.item.id, value)" />
      </template>
      <template #cell(status)="data">
        {{ statusLabel(data.item.status) }}
      </template>
      <template #cell(date)="data">
        {{ formatAppointmentDate(data.item) }}
      </template>
      <template #cell(from)="data">
        {{ formatTime(data.item.startTime) }}
      </template>
      <template #cell(to)="data">
        {{ formatTime(data.item.endTime) }}
      </template>
      <template #cell(registrations)="data">
        {{ formatRegistrations(data.item) }}
      </template>
      <template #cell(participants)="data">
        {{ participantsLabel(data.item) }}
      </template>
      <template #cell(assignee)="data">
        {{ assigneeName(data.item) }}
      </template>
    </b-table>
    <b-pagination
      v-model="currentPage"
      align="center"
      class="mt-3 mb-0"
      :total-rows="appointments.length"
      :per-page="nbrPerPage" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import CoozzyDropdown from '@/framework/components/dropdown/CoozzyDropdown'
import CoozzyDropdownItem from '@/framework/components/dropdown/CoozzyDropdownItem'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon'
import ContactApi from '@/misc/apis/ContactApi'
import { dateUtils } from '@/mixins/dateUtils'
import { tables } from '@/mixins/tables'
import { padTimePart } from '@/misc/utils/viewingAppointmentSlotUtils'
import { SchedulerAppointmentStatus } from '@/types/api'

export default {
  name: 'ViewingAppointmentsTable',
  components: {
    CoozzyFormSelect,
    CoozzyFormCheckbox,
    CoozzySpinner,
    CoozzyDropdown,
    CoozzyDropdownItem,
    CoozzyInfoCircleIcon
  },
  mixins: [dateUtils, tables],
  props: {
    appointments: {
      type: Array,
      default: () => []
    },
    busy: {
      type: Boolean,
      default: false
    },
    viewingContactId: {
      type: String,
      default: ''
    },
    viewingContactName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      nbrPerPage: 5,
      currentPage: 1,
      selectedEntries: [],
      contactNameById: {},
      fields: [
        {
          key: 'checkbox',
          label: '',
          thClass: 'th-checkbox align-middle',
          tdClass: 'align-middle border-right-0 checkbox-cell'
        },
        {
          key: 'status',
          label: this.$t('message.marketingObjects.viewingAppointments.columns.status'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'date',
          label: this.$t('message.marketingObjects.viewingAppointments.date'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'from',
          label: this.$t('message.marketingObjects.viewingAppointments.from'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'to',
          label: this.$t('message.marketingObjects.viewingAppointments.to'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'registrations',
          label: this.$t('message.marketingObjects.viewingAppointments.columns.registrations'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'participants',
          label: this.$t('message.marketingObjects.viewingAppointments.columns.participants'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'assignee',
          label: this.$t('message.marketingObjects.viewingAppointments.columns.assignee'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('employee', ['getEmployees']),
    selectedEntriesText() {
      return this.$t('message.generic.selectedEntries') + ':  ' + this.selectedEntries.length
    },
    selectedAppointments() {
      return this.appointments.filter(appointment => this.selectedEntries.includes(appointment.id))
    },
    allPageSelected() {
      const pageIds = this.pagedAppointments.map(appointment => appointment.id)
      return pageIds.length > 0 && pageIds.every(id => this.selectedEntries.includes(id))
    },
    hasNonScheduledSelection() {
      return this.selectedAppointments.some(appointment =>
        appointment.status !== SchedulerAppointmentStatus.APPOINTMENT_STATUS_SCHEDULED
      )
    },
    canCancelAppointments() {
      return this.selectedEntries.length > 0 && !this.hasNonScheduledSelection
    },
    showCancelScheduledOnlyTooltip() {
      return this.selectedEntries.length > 0 && this.hasNonScheduledSelection
    },
    canWriteSerialEmail() {
      return this.selectedEntries.length > 0 && this.selectedParticipantEmailList.length > 0
    },
    selectedParticipantEmailList() {
      const emails = []
      this.selectedAppointments.forEach(appointment => {
        ;(appointment.participants || []).forEach(participant => {
          const email = (participant.email || '').trim()
          if (email && !emails.includes(email)) {
            emails.push(email)
          }
        })
      })
      return emails
    },
    sortedAppointments() {
      return this.appointments.slice().sort((a, b) => {
        const rankDiff = this.statusRank(a?.status) - this.statusRank(b?.status)
        if (rankDiff !== 0) {
          return rankDiff
        }
        const dateA = this.objectToIsoDate(a?.date) || ''
        const dateB = this.objectToIsoDate(b?.date) || ''
        if (dateA !== dateB) {
          return dateA.localeCompare(dateB)
        }
        const timeA = this.timeToSortValue(a?.startTime)
        const timeB = this.timeToSortValue(b?.startTime)
        return timeA - timeB
      })
    },
    pagedAppointments() {
      const start = (this.currentPage - 1) * this.nbrPerPage
      return this.sortedAppointments.slice(start, start + this.nbrPerPage)
    },
    employeeList() {
      return (this.getEmployees || []).filter(e => !e.deleted && !e.blocked)
    }
  },
  watch: {
    appointments() {
      this.selectedEntries = this.selectedEntries.filter(id =>
        this.appointments.some(appointment => appointment.id === id)
      )
      this.resolveCreatorContacts()
      if (this.currentPage > 1 && this.pagedAppointments.length === 0) {
        this.currentPage = 1
      }
      this.$emit('selection-changed', this.selectedEntries.slice())
    },
    nbrPerPage() {
      this.currentPage = 1
    }
  },
  mounted() {
    this.loadEmployees().catch(e => console.log(e))
    this.resolveCreatorContacts()
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    statusRank(status) {
      if (status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_SCHEDULED) {
        return 0
      }
      if (status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_CONDUCTED) {
        return 1
      }
      if (status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_CANCELLED) {
        return 2
      }
      return 3
    },
    timeToSortValue(time) {
      if (!time) {
        return 0
      }
      return (time.hours || 0) * 60 + (time.minutes || 0)
    },
    clearSelection() {
      this.selectedEntries = []
      if (this.$refs.header_checkbox) {
        this.$refs.header_checkbox.checked = false
      }
      this.$emit('selection-changed', [])
    },
    rowClass(item) {
      if (item?.status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_CANCELLED) {
        return 'viewing-appointment-cancelled-row'
      }
      return null
    },
    statusLabel(status) {
      if (status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_CONDUCTED) {
        return this.$t('message.marketingObjects.viewingAppointments.statuses.conducted')
      }
      if (status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_CANCELLED) {
        return this.$t('message.marketingObjects.viewingAppointments.statuses.cancelled')
      }
      return this.$t('message.marketingObjects.viewingAppointments.statuses.scheduled')
    },
    formatAppointmentDate(appointment) {
      if (!appointment?.date) {
        return '-'
      }
      return this.objectToStringDate(appointment.date)
    },
    formatTime(time) {
      if (!time) {
        return '-'
      }
      return padTimePart(time.hours || 0) + ':' + padTimePart(time.minutes || 0)
    },
    participantCount(appointment) {
      return (appointment.participants || []).length
    },
    formatRegistrations(appointment) {
      const count = this.participantCount(appointment)
      const max = appointment?.maxParticipants
      if (!max) {
        return count + '/-'
      }
      return count + '/' + max
    },
    participantsLabel(appointment) {
      return (appointment.participants || [])
        .map(participant => [participant.firstName, participant.lastName].filter(Boolean).join(' ').trim())
        .filter(Boolean)
        .join(', ')
    },
    employeeDisplayName(employee) {
      return [employee?.profile?.firstName, employee?.profile?.lastName].filter(Boolean).join(' ').trim()
    },
    assigneeName(appointment) {
      const calendarUserId = appointment?.calendarUserId
      if (calendarUserId) {
        const employee = this.employeeList.find(e => e.id === calendarUserId)
        return employee ? this.employeeDisplayName(employee) : ''
      }
      if (this.viewingContactName) {
        return this.viewingContactName
      }
      const creatorId = appointment?.creatorId
      if (creatorId && this.contactNameById[creatorId]) {
        return this.contactNameById[creatorId]
      }
      return ''
    },
    resolveCreatorContacts() {
      const employeeIds = new Set(this.employeeList.map(e => e.id))
      const contactIds = [...new Set(
        this.appointments
          .filter(appointment => !appointment.calendarUserId)
          .map(appointment => appointment.creatorId)
          .filter(id => id && !employeeIds.has(id) && !this.contactNameById[id])
      )]
      if (!contactIds.length) {
        return
      }
      ContactApi.contactResolve(contactIds)
        .then(response => {
          const contacts = (response.persons || []).concat(response.companies || [])
          contacts.forEach(contact => {
            const name = contact.name ||
              [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim()
            if (contact.id && name) {
              this.$set(this.contactNameById, contact.id, name)
            }
            ;(contact.contactPersons || []).forEach(person => {
              const personName = person.name ||
                [person.firstName, person.lastName].filter(Boolean).join(' ').trim()
              if (person.id && personName) {
                this.$set(this.contactNameById, person.id, personName)
              }
            })
          })
        })
        .catch(e => console.log(e))
    },
    selectAppointment(appointmentId, checked) {
      if (checked) {
        if (!this.selectedEntries.includes(appointmentId)) {
          this.selectedEntries.push(appointmentId)
        }
      } else {
        this.selectedEntries = this.selectedEntries.filter(id => id !== appointmentId)
      }
      this.$emit('selection-changed', this.selectedEntries.slice())
    },
    onSelectAll(value) {
      const pageIds = this.pagedAppointments.map(appointment => appointment.id)
      if (value) {
        pageIds.forEach(id => {
          if (!this.selectedEntries.includes(id)) {
            this.selectedEntries.push(id)
          }
        })
      } else {
        this.selectedEntries = this.selectedEntries.filter(id => !pageIds.includes(id))
      }
      this.$emit('selection-changed', this.selectedEntries.slice())
    },
    onRowClicked(item, _index, event) {
      const target = event?.target
      if (target?.closest?.('.checkbox-cell') || target?.type === 'checkbox') {
        return
      }
      this.$emit('row-clicked', item)
    },
    onCancelAppointments() {
      if (!this.canCancelAppointments) {
        return
      }
      this.$emit('cancel-appointments', this.selectedAppointments.slice())
    },
    onSerialEmail() {
      if (!this.canWriteSerialEmail) {
        return
      }
      this.$emit('serial-email', this.selectedAppointments.slice())
    }
  }
}
</script>

<style scoped>
:deep(.checkbox-cell) {
  width: 2.5rem;
}
:deep(.viewing-appointment-cancelled-row) {
  color: #6c757d;
  opacity: 0.65;
}
</style>
